// @flow

import { Fragment } from 'react';

/** @jsx jsx */
import { Colors } from '@zapier/theme';
import { DateTime } from '@zapier/date';
import { Link } from '@zapier/common-components';
import { jsx } from '@emotion/core';
import { MeteredTiersTable, Price } from '@zapier/billing';

import type { Interval, PlanTier } from '@zapier/design-system-revenue';

type AmountDueOnNextIntervalProps = {
  isPlanMetered: boolean,
  meteredTiers: PlanTier[],
  nextPaymentDate: string,
  planBillingAmount: number,
  planInterval: Interval,
  planMeteredCostPerTask: number,
  planNumFreeMeteredTasks: number,
};

const nextIntervalCss = {
  backgroundColor: Colors.storm,
  color: Colors.steel,
  padding: 10,
};

const lastParagraphCss = {
  margin: 0,
};

const tableStyle = {
  marginBottom: 15,
};

const AmountDueOnNextInterval = ({
  isPlanMetered,
  meteredTiers,
  nextPaymentDate,
  planBillingAmount,
  planInterval,
  planNumFreeMeteredTasks,
}: AmountDueOnNextIntervalProps) => {
  const amount = (
    <strong>
      <Price>{planBillingAmount}</Price>
    </strong>
  );

  return (
    <div css={nextIntervalCss}>
      <p>
        You authorize Zapier to charge you for the amount above. The next charge
        will be for {amount}
        {nextPaymentDate && (
          <Fragment>
            {' '}
            on{' '}
            <strong>
              <DateTime date={nextPaymentDate} />
            </strong>
          </Fragment>
        )}
        . Your subscription renews each {planInterval} until you cancel.{' '}
        {isPlanMetered && (
          <Fragment>
            Your plan includes{' '}
            <strong>{planNumFreeMeteredTasks.toLocaleString()} tasks</strong>{' '}
            per month. Extra tasks used will be charged monthly at the below
            rates.
          </Fragment>
        )}
      </p>
      {isPlanMetered && (
        <div css={tableStyle}>
          <MeteredTiersTable data={meteredTiers} />
        </div>
      )}
      <p css={lastParagraphCss}>
        By clicking the button below, you understand and agree to these terms
        and Zapier’s{' '}
        <Link href="/privacy" target="_blank">
          privacy policy
        </Link>{' '}
        and{' '}
        <Link href="/terms" target="_blank">
          terms of service
        </Link>
        .
      </p>
    </div>
  );
};

export default AmountDueOnNextInterval;
